/**
 *  @class
 *  @function BackToTop
 */
if (!customElements.get('search-form')) {
  class SearchForm extends HTMLElement {
    constructor() {
      super();

    }
    connectedCallback() {
      this.is_drawer = this.dataset.drawer;
      this.drawer = this.closest('#Search-Drawer');
      this.form = this.querySelector('form');
      this.button = document.querySelectorAll('.thb-quick-search');
      this.input = this.querySelector('.search-field');
      this.defaultTab = this.querySelector('.side-panel-content--initial');
      this.popularSearches = this.querySelector('[data-search-drawer-popular]');
      this.predictiveSearchResults = this.is_drawer
        ? this.querySelector('.side-panel-content--has-tabs.thb-predictive-search')
        : this.querySelector('.thb-predictive-search');
      this.cc = this.querySelector('.searchform--click-capture');
      this.closeButton = this.querySelector('.search-drawer__close');
      this.recommendationTabs = Array.from(this.querySelectorAll('[data-search-drawer-tab]'));
      this.recommendationPanels = Array.from(this.querySelectorAll('[data-search-drawer-panel]'));
      this.recentlyViewedPanel = this.querySelector('[data-recently-viewed-panel]');
      this.recentlyViewedProducts = this.recentlyViewedPanel?.querySelector('.products');
      this.recentlyViewedEmpty = this.recentlyViewedPanel?.querySelector('[data-recently-viewed-empty]');
      this.recentlyViewedLoaded = false;
      this.defaultResultsMarkup = this.defaultTab ? this.defaultTab.innerHTML : '';

      this.setupEventListeners();
      this.loadRecentlyViewedProducts();
    }

    setupEventListeners() {
      this.form.addEventListener('submit', this.onFormSubmit.bind(this));

      if (this.is_drawer && this.popularSearches) {
        this.input.addEventListener('click', this.revealPopularSearches.bind(this), { once: true });
      }

      if (this.predictiveSearchResults) {
        this.input.addEventListener('input', debounce((event) => {
          this.onChange(event);
        }, 300).bind(this));
        this.input.addEventListener('focus', debounce((event) => {
          this.onChange(event);
        }, 300).bind(this));
      }

      this.button.forEach((item, i) => {
        item.addEventListener('click', (event) => {
          event.preventDefault();
          if (this.is_drawer && (this.drawer.classList.contains('active') || document.body.classList.contains('open-search-drawer'))) {
            return false;
          }

          document.body.classList.add('open-cc', 'open-search-drawer');
          if (this.is_drawer) {
            this.drawer.classList.add('active');
            setTimeout(() => {
              this.input.focus();
            }, 80);
          }
          return false;
        });
      });

      this.cc?.addEventListener('click', this.close.bind(this));
      this.closeButton?.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.closeButton.click();
        }
      });

      this.recommendationTabs.forEach((tab) => {
        tab.addEventListener('click', () => {
          this.activateRecommendationTab(tab);
        });
        tab.addEventListener('keydown', (event) => {
          this.onRecommendationTabKeydown(event);
        });
      });

      document.addEventListener('keyup', (e) => {
        if (e.code && e.code.toUpperCase() === 'ESCAPE') {
          this.close();
        }
      });
    }

    revealPopularSearches() {
      this.popularSearches?.classList.add('is-visible');
      this.popularSearches?.setAttribute('aria-hidden', 'false');
    }

    activateRecommendationTab(activeTab) {
      const activePanelId = activeTab.getAttribute('aria-controls');

      this.recommendationTabs.forEach((tab) => {
        const isActive = tab === activeTab;
        tab.classList.toggle('active', isActive);
        tab.setAttribute('aria-selected', isActive.toString());
        tab.tabIndex = isActive ? 0 : -1;
      });

      this.recommendationPanels.forEach((panel) => {
        const isActive = panel.id === activePanelId;
        panel.classList.toggle('active', isActive);
        panel.hidden = !isActive;
      });

      if (activePanelId === this.recentlyViewedPanel?.id) {
        this.loadRecentlyViewedProducts();
      }
    }

    onRecommendationTabKeydown(event) {
      const currentIndex = this.recommendationTabs.indexOf(event.currentTarget);
      let nextIndex = currentIndex;

      if (event.key === 'ArrowRight') {
        nextIndex = (currentIndex + 1) % this.recommendationTabs.length;
      } else if (event.key === 'ArrowLeft') {
        nextIndex = (currentIndex - 1 + this.recommendationTabs.length) % this.recommendationTabs.length;
      } else if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = this.recommendationTabs.length - 1;
      } else {
        return;
      }

      event.preventDefault();
      this.recommendationTabs[nextIndex].focus();
      this.activateRecommendationTab(this.recommendationTabs[nextIndex]);
    }

    loadRecentlyViewedProducts() {
      if (!this.recentlyViewedPanel || !this.recentlyViewedProducts || this.recentlyViewedLoaded) {
        return;
      }

      this.recentlyViewedLoaded = true;
      let recentIds = [];

      try {
        const storedRecentIds = window.localStorage?.getItem('recently-viewed');
        const parsedRecentIds = storedRecentIds ? JSON.parse(storedRecentIds) : [];
        if (Array.isArray(parsedRecentIds)) {
          recentIds = parsedRecentIds;
        }
      } catch (error) {
        recentIds = [];
      }

      recentIds = recentIds.filter((handle, index, handles) => {
        return typeof handle === 'string' && handle.length > 0 && handles.indexOf(handle) === index;
      });

      const productLimit = Number.parseInt(this.recentlyViewedPanel.dataset.productLimit, 10) || 12;
      window.recentlyViewedIds = recentIds;
      const recentIdsForDisplay = recentIds.slice(0, productLimit);

      if (!recentIdsForDisplay.length) {
        this.showRecentlyViewedEmpty();
        return;
      }

      const productQuery = recentIdsForDisplay.map((handle) => `handle:${handle}`).join(' OR ');
      const recentlyViewedUrl = `${theme.routes.search_url}?view=recently-viewed&type=product&q=${encodeURIComponent(productQuery)}`;

      fetch(recentlyViewedUrl)
        .then((response) => {
          if (!response.ok) {
            throw new Error(response.status);
          }
          return response.text();
        })
        .then((text) => {
          const products = new DOMParser().parseFromString(text, 'text/html').querySelector('.products');

          if (!products || !products.innerHTML.trim().length) {
            this.showRecentlyViewedEmpty();
            return;
          }

          this.recentlyViewedProducts.innerHTML = products.innerHTML;
          this.recentlyViewedPanel.setAttribute('aria-busy', 'false');
          if (this.recentlyViewedEmpty) {
            this.recentlyViewedEmpty.hidden = true;
          }
        })
        .catch(() => {
          this.showRecentlyViewedEmpty();
        });
    }

    showRecentlyViewedEmpty() {
      if (!this.recentlyViewedPanel) {
        return;
      }

      this.recentlyViewedPanel.setAttribute('aria-busy', 'false');
      if (this.recentlyViewedProducts) {
        this.recentlyViewedProducts.innerHTML = '';
      }
      if (this.recentlyViewedEmpty) {
        this.recentlyViewedEmpty.hidden = false;
      }
    }

    getQuery() {
      return this.input.value.trim();
    }

    resetDrawerResults() {
      if (!this.predictiveSearchResults) {
        return;
      }

      this.predictiveSearchResults.classList.remove('active', 'loading');

      if (this.is_drawer && this.defaultTab?.contains(this.predictiveSearchResults)) {
        this.defaultTab.innerHTML = this.defaultResultsMarkup;
        this.predictiveSearchResults = this.defaultTab.querySelector('.thb-predictive-search');
        return;
      }

      this.predictiveSearchResults.innerHTML = '';
    }

    onChange() {
      if (!this.predictiveSearchResults) {
        return;
      }

      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        if (!this.is_drawer) {
          this.close();
        } else {
          this.resetDrawerResults();
        }
        return;
      }
      if (!this.is_drawer) {
        this.open();
      }
      this.predictiveSearchResults.classList.add('active');
      this.getSearchResults(searchTerm);
    }

    onFormSubmit(event) {
      if (!this.getQuery().length) {
        event.preventDefault();
      }
    }

    onFocus() {
      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        this.predictiveSearchResults.innerHTML = '';
        return;
      }

      this.getSearchResults(searchTerm);
    }

    getSearchResults(searchTerm) {
      if (!this.predictiveSearchResults) {
        return;
      }

      this.predictiveSearchResults.classList.add('loading');

      fetch(`${theme.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product,article,query,page&${encodeURIComponent('resources[limit]')}=10&resources[options][fields]=title,product_type,vendor,variants.title,variants.sku&section_id=predictive-search`)
        .then((response) => {
          this.predictiveSearchResults.classList.remove('loading');
          if (!response.ok) {
            var error = new Error(response.status);
            throw error;
          }

          return response.text();
        })
        .then((text) => {
          if (this.getQuery() !== searchTerm) {
            return;
          }
          const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;

          this.renderSearchResults(resultsMarkup);
        })
        .catch((error) => {
          throw error;
        });
    }

    renderSearchResults(resultsMarkup) {
      if (!this.predictiveSearchResults) {
        return;
      }

      this.predictiveSearchResults.innerHTML = resultsMarkup;
      if (!this.is_drawer) {
        this.predictiveSearchResults.querySelector('#search-results-submit').classList.remove('button');
      }
      this.predictiveSearchResults.querySelector('#search-results-submit').addEventListener('click', () => {
        this.form.submit();
      });
    }

    close() {
      this.predictiveSearchResults?.setAttribute('inert', '');
      document.body.classList.remove('open-search');
      if (this.is_drawer) {
        this.drawer.classList.remove('active');
        document.body.classList.remove('open-cc', 'open-search-drawer');
      }
    }
    open() {
      document.body.classList.add('open-search');
      this.predictiveSearchResults?.removeAttribute('inert');
    }
  }
  customElements.define('search-form', SearchForm);
}
